import type { CreateHabitRequest, Weekday, TimeWindow } from '../services/habitService'
import type { HabitScheduleValue } from '../components/habits/HabitScheduleEditor'
import { buildRRule, defaultSpec, deriveLegacyCadence, deriveLegacyPreferredDays, deriveLegacyTimeWindow, type RRuleSpec, type RWeekday } from './rrule'

/**
 * The create form's vocabulary and its one mapping to the API (Habits UX pass
 * 2026-09-06). A title is enough; the quick schedule chooser writes a few
 * plain keys, "More options" writes the full editor's value, and this is the
 * only place either becomes an RRULE.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type HabitFormData = Record<string, any>

export type ScheduleMode = '' | 'daily' | 'weekdays' | 'weekends' | 'days'
export const SCHEDULE_MODE_OPTIONS: Array<{ value: ScheduleMode; label: string }> = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekends', label: 'Weekends' },
  { value: 'days', label: 'Some days' },
]
export const WEEKDAY_CHIPS: Array<{ value: RWeekday; label: string }> = [
  { value: 'MO', label: 'Mon' }, { value: 'TU', label: 'Tue' }, { value: 'WE', label: 'Wed' }, { value: 'TH', label: 'Thu' },
  { value: 'FR', label: 'Fri' }, { value: 'SA', label: 'Sat' }, { value: 'SU', label: 'Sun' },
]

function todayLocalISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** The RRULE spec the quick chooser means, or null when nothing was chosen. */
export function quickSpec(form: HabitFormData): RRuleSpec | null {
  const mode = (form.schedule_mode || '') as ScheduleMode
  if (!mode) return null
  if (mode === 'daily') return { ...defaultSpec(), freq: 'DAILY' }
  const days: RWeekday[] = mode === 'weekdays' ? ['MO', 'TU', 'WE', 'TH', 'FR'] : mode === 'weekends' ? ['SA', 'SU'] : (form.schedule_days || [])
  if (days.length === 0) return null
  return { ...defaultSpec(), freq: 'WEEKLY', byweekday: days }
}

/** What the form sends. Nothing optional is sent when it was not said. */
export function habitFormToPayload(form: HabitFormData): CreateHabitRequest {
  const parentId = form.parent_id ? Number(form.parent_id) : null
  const areas: string[] = Array.isArray(form.life_areas) ? form.life_areas.map(String) : []
  const payload: CreateHabitRequest = {
    title: String(form.title || '').trim(),
    priority: (form.priority || 'medium') as CreateHabitRequest['priority'],
    parent_goal_id: form.parent_type === 'goal' && parentId ? parentId : undefined,
    parent_skill_id: form.parent_type === 'skill' && parentId ? parentId : undefined,
    life_areas: !parentId && areas.length ? areas : undefined,
    life_area: !parentId && areas.length ? areas[0] : undefined,
    description: form.description?.trim?.() || undefined,
    minimum_viable_repetition: form.minimum_viable_repetition?.trim?.() || undefined,
    cue_or_anchor: form.cue_or_anchor?.trim?.() || undefined,
    duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : undefined,
  }
  const full = form.schedule_advanced ? (form.schedule_full as HabitScheduleValue | undefined) : undefined
  if (full) {
    payload.rrule = full.rrule
    payload.scheduled_times_local = full.scheduled_times_local
    payload.schedule_start_date = full.schedule_start_date
    payload.duration_minutes = full.duration_minutes || payload.duration_minutes
    payload.target_cadence = full.target_cadence
    payload.preferred_days = full.preferred_days as Weekday[]
    payload.preferred_time_window = full.preferred_time_window as TimeWindow
    return payload
  }
  const spec = quickSpec(form)
  if (spec) {
    const time = String(form.schedule_time || '').trim()
    payload.rrule = buildRRule(spec)
    payload.scheduled_times_local = time ? [time] : []
    payload.schedule_start_date = todayLocalISO()
    payload.target_cadence = deriveLegacyCadence(spec)
    payload.preferred_days = deriveLegacyPreferredDays(spec) as Weekday[]
    if (time) payload.preferred_time_window = deriveLegacyTimeWindow(time) as TimeWindow
  } else if (String(form.schedule_time || '').trim()) {
    // A time without days: the engine treats it as a daily start time, as prod's chat-made habits do.
    payload.preferred_start_time = String(form.schedule_time).trim()
  }
  return payload
}
